// ─────────────────────────────────────────────────────────────────────────
// societe-interne.js — Aureus IA SRL, NOTRE société, n'est pas un dossier client.
//
// Elle existe à deux endroits :
//   • une ligne de la table `clients` (même BCE que nous) ;
//   • l'option « mode global » du sélecteur de sociétés (valeur vide).
// Les deux donnent accès à NOS données internes (matricule ONSS, mandats,
// intégrations, paie interne) — pas à celles d'un client.
//
// Règle (fondateur, 2026-08-04) : un compte habilité « tous les dossiers »
// (app_metadata.all_clients — partenaire) voit tous les dossiers CLIENTS, et
// PAS la société interne. L'habilitation sert le portefeuille clients ; nos
// données internes restent au staff qui y a droit par son rôle.
//
// Le numéro BCE vit ICI, une seule fois : il était recopié dans le sélecteur.
// ─────────────────────────────────────────────────────────────────────────

import { numerosEntreprise } from './client-identite.js'; // relatif : chargé en Node brut via tenant-config → agents-registry (scripts/smoke-agents.mjs)

// BCE d'Aureus IA SRL, chiffres seuls (les écrans l'affichent « BE 1028.230.781 »).
export const BCE_SOCIETE_INTERNE = '1028230781';

// Raison sociale de NOTRE société — source unique, pour les documents où Aureus
// est LÉGITIMEMENT le sujet (débiteur de sa propre déclaration 281.50, créancier
// SEPA…), jamais comme repli d'identité d'un employeur client (garde-fou
// __tests__/identite-employeur-pas-aureus.test.js).
export const NOM_SOCIETE_INTERNE = 'Aureus IA SRL';

/** Chiffres seuls d'un numéro BCE, quel que soit son formatage. */
export function bceChiffres(bce) {
  return String(bce || '').replace(/\D/g, '');
}

/**
 * Cette ligne `clients` est-elle NOTRE société ?
 *
 * ⚠️ Corrigé le 14/08/2026 : ne regardait QUE la colonne `bce`. Or le numéro
 * d'entreprise vit dans TROIS colonnes selon le chemin d'écriture (`bce`,
 * `vat`, `company_vat`) et dans `data` — c'est le problème que
 * `client-identite.js` existe pour résoudre, et il n'était pas branché ici.
 * Conséquence : notre propre société, si son numéro n'était renseigné que dans
 * `vat`, n'était PAS reconnue comme interne — donc elle apparaissait dans le
 * portefeuille d'un compte « tous les dossiers », et le repli de matricule qui
 * s'appuie sur cette fonction se serait trompé de sens.
 *
 * @param {{bce?: string, vat?: string, company_vat?: string, data?: object}|null} client
 */
export function isSocieteInterne(client) {
  if (!client) return false;
  return numerosEntreprise(client).includes(BCE_SOCIETE_INTERNE);
}

/**
 * Ids des fiches `clients` qui SONT notre société (même BCE). Pluriel : la
 * fiche a existé en double, et le sélecteur les masque toutes (layout-client).
 * @param {Array<{id?: string, bce?: string}>|null} clients
 */
export function idsSocieteInterne(clients) {
  return (Array.isArray(clients) ? clients : [])
    .filter((c) => isSocieteInterne(c))
    .map((c) => c.id)
    .filter(Boolean);
}

/**
 * Périmètre d'AFFICHAGE des employés — convention UNIQUE, écrite ici une fois.
 *
 *   • société active (activeClientId) → SES employés (client_id égal) ;
 *   • mode global (société interne, activeClientId vide) → les employés
 *     INTERNES : sans client_id (historique d'avant le dossier propre) OU
 *     rattachés à la fiche `clients` de notre société (même BCE).
 *
 * POURQUOI (bug du 14/08/2026, écran Fiches PDF « TRAVAILLEURS 0 ») : la
 * convention front disait « mode global = client_id NULL », datée d'AVANT la
 * création du dossier propre Aureus (#914) et le rattachement des employés
 * internes. /api/employees, lui, renvoyait déjà « NULL OU fiche interne » :
 * l'API livrait l'employé, l'écran le jetait. Tout écran qui re-filtre
 * state.emps passe par ICI — jamais par un `!e.client_id` local.
 *
 * `clients` absent/vide → seuls les sans-client_id passent (comportement
 * d'avant, dégradation douce le temps que la liste charge).
 */
export function empsDuPerimetre(emps, activeClientId, clients) {
  const list = Array.isArray(emps) ? emps : [];
  const cidDe = (e) => (e && typeof e === 'object' ? e.client_id || e.clientId || null : null);
  if (activeClientId) return list.filter((e) => cidDe(e) === activeClientId);
  const internes = new Set(idsSocieteInterne(clients));
  return list.filter((e) => {
    const cid = cidDe(e);
    return !cid || internes.has(cid);
  });
}
